import { useState, useRef, useEffect, Fragment } from 'react';
import { ChevronDown, ChevronRight, Check, Search } from 'lucide-react';

const SIZE_CLASSES = {
  sm: {
    button: 'px-2.5 py-1.5 text-xs rounded-md',
    chevron: 'w-3.5 h-3.5',
    dropdown: 'rounded-md',
    option: 'px-2.5 py-1.5 text-xs',
    checkbox: 'w-3.5 h-3.5',
    checkIcon: 'w-2.5 h-2.5',
  },
  md: {
    button: 'px-3 py-2 text-sm rounded-lg',
    chevron: 'w-4 h-4',
    dropdown: 'rounded-lg',
    option: 'px-3 py-1.5 text-sm',
    checkbox: 'w-4 h-4',
    checkIcon: 'w-3 h-3',
  },
};

const SEARCH_THRESHOLD = 20;

export default function MultiSelectFilter({ options, selected, onChange, allLabel, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(null); // null = use default
  const ref = useRef(null);
  const searchRef = useRef(null);
  const s = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const showSearch = options.length > SEARCH_THRESHOLD;

  // Detect if options use groups
  const hasGroups = options.length > 0 && typeof options[0] === 'object' && options[0].group;

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (open && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
    if (!open) setSearch('');
  }, [open, showSearch]);

  // Initialize collapsed state: all groups collapsed by default, except when only 1 group
  useEffect(() => {
    if (!hasGroups || collapsedGroups !== null) return;
    const groups = new Set(options.map(o => o.group).filter(Boolean));
    if (groups.size <= 1) {
      setCollapsedGroups(new Set()); // single group = expanded
    } else {
      setCollapsedGroups(new Set(groups)); // all collapsed
    }
  }, [hasGroups, options, collapsedGroups]);

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const getLabel = (value) => {
    if (typeof options[0] !== 'object') return value;
    return options.find(o => o.value === value)?.label || value;
  };

  const summary = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? getLabel(selected[0])
      : `${selected.length} selected`;

  const filtered = showSearch && search
    ? options.filter(opt => {
        const label = typeof opt === 'object' ? opt.label : opt;
        const group = typeof opt === 'object' ? opt.group : '';
        return label.toLowerCase().includes(search.toLowerCase()) ||
               group.toLowerCase().includes(search.toLowerCase());
      })
    : options;

  // When searching, expand all groups so matches are visible
  const isSearching = showSearch && search.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--text-primary)] focus:ring-2 focus:ring-cgiar-green focus:border-transparent cursor-pointer ${s.button}`}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className={`${s.chevron} ml-1 flex-shrink-0 text-[var(--text-tertiary)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-50 mt-1 w-full bg-[var(--surface-primary)] border border-[var(--border-primary)] shadow-lg max-h-72 overflow-y-auto ${s.dropdown}`}>
          {showSearch && (
            <div className="sticky top-0 bg-[var(--surface-primary)] border-b border-[var(--border-primary)] px-2 py-1.5">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-7 pr-2 py-1 text-xs bg-[var(--input-bg)] border border-[var(--input-border)] rounded text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-1 focus:ring-cgiar-green"
                />
              </div>
            </div>
          )}
          {filtered.map((opt, i) => {
            const value = typeof opt === 'object' ? opt.value : opt;
            const optLabel = typeof opt === 'object' ? opt.label : opt;
            const checked = selected.includes(value);
            const group = typeof opt === 'object' ? opt.group : null;
            const prevGroup = i > 0 ? (typeof filtered[i - 1] === 'object' ? filtered[i - 1].group : null) : null;
            const showGroupHeader = hasGroups && group && group !== prevGroup;
            const isCollapsed = hasGroups && group && !isSearching && collapsedGroups?.has(group);

            return (
              <Fragment key={value}>
                {showGroupHeader && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-left text-[var(--text-secondary)] bg-[var(--surface-secondary,#f8fafc)] border-t border-[var(--border-primary)] hover:bg-[var(--surface-hover)] cursor-pointer"
                  >
                    {isSearching || !collapsedGroups?.has(group)
                      ? <ChevronDown className="w-3 h-3 flex-shrink-0" />
                      : <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    }
                    {group}
                  </button>
                )}
                {!isCollapsed && (
                  <button
                    type="button"
                    onClick={() => toggle(value)}
                    className={`w-full flex items-center gap-2 text-[var(--text-primary)] hover:bg-[var(--surface-hover)] text-left ${s.option} ${hasGroups ? 'pl-5' : ''}`}
                  >
                    <span className={`${s.checkbox} flex-shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-cgiar-green border-cgiar-green text-white' : 'border-[var(--input-border)]'}`}>
                      {checked && <Check className={s.checkIcon} />}
                    </span>
                    <span className="truncate">{optLabel}</span>
                  </button>
                )}
              </Fragment>
            );
          })}
          {showSearch && search && filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-[var(--text-tertiary)] text-center">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}
